import { useEffect, useCallback, useRef } from 'react';

const useLastActiveTracker = () => {
    const lastUpdateRef = useRef(0);
    const timeoutRef = useRef(null);

    const updateLastActive = useCallback(async () => {
        const now = Date.now();

        if (now - lastUpdateRef.current < 5 * 60 * 1000) {
            return;
        }

        try {
            const token = localStorage.getItem("token");
            if (!token) return;

            const response = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/update-last-active`,
                {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (response.ok) {
                lastUpdateRef.current = now;
            }
        } catch (error) {
            console.error("Failed to update last active:", error);
        }
    }, []);

    const scheduleUpdate = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
            updateLastActive();
        }, 30000);
    }, [updateLastActive]);

    const handleActivity = useCallback(() => {
        scheduleUpdate();
    }, [scheduleUpdate]);

    useEffect(() => {
        updateLastActive();

        const events = [
            'mousedown',
            'mousemove',
            'keypress',
            'scroll',
            'touchstart',
            'click',
            'focus'
        ];

        events.forEach(event => {
            document.addEventListener(event, handleActivity, { passive: true });
        });

        const handleVisibilityChange = () => {
            if (!document.hidden) {
                updateLastActive();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            events.forEach(event => {
                document.removeEventListener(event, handleActivity);
            });
            document.removeEventListener('visibilitychange', handleVisibilityChange);

            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [handleActivity, updateLastActive]);

    return { updateLastActive };
};

export default useLastActiveTracker;
